import SubscriptionPlan from '@/models/SubscriptionPlan';
import _api from '../_api';
import { buildActivationPeriods, } from '../auditLogs';
import { isInternalTestPlan, } from '../internalTestPlans';

const ROLLBACK_FAILED_MESSAGE = 'Não foi possível ativar o novo plano nem ' +
  'restaurar o anterior. No momento não há nenhum plano ativo nesta seção — ' +
  'ative um manualmente.';

/*
 * The BFF wraps the list in a `data` envelope, which lands nested inside
 * axios' own `data`. Tolerates a bare array so an envelope-less response
 * doesn't silently blank the table.
 */
const unwrapList = (response,) => {
  const body = (response && response.data) || [];

  return Array.isArray(body,) ? body : body.data || [];
};

const unwrapItem = (response,) => {
  const body = (response && response.data) || {};

  return body.data || body;
};

/**
 * Loads one of the two listings.
 *
 * @param {Boolean} hasTrial `true` for free plans, `false` for paid ones
 */
const fetchGroup = (hasTrial,) => {
  return _api.get({ has_trial: hasTrial, },).
    then((response,) => unwrapList(response,).
      /* TEMPORARY: see ../internalTestPlans.js */
      filter((plan,) => !isInternalTestPlan(plan,),).
      map((plan,) => new SubscriptionPlan(plan, { hasTrial, },),),);
};

export default {
  namespaced: true,
  // -----------------------------------------------------------------
  state: {
    paidPlans: [],
    freePlans: [],
    details: null,
    history: null,
    isLoading: false,
    isLoadingDetails: false,
  },
  // -----------------------------------------------------------------
  getters: {
    paidPlans: (state,) => state.paidPlans,
    freePlans: (state,) => state.freePlans,
    activePaidPlan: (state,) => {
      return state.paidPlans.find((plan,) => plan.isActive,) || null;
    },
    details: (state,) => state.details,
    history: (state,) => state.history,
    isLoading: (state,) => state.isLoading,
    isLoadingDetails: (state,) => state.isLoadingDetails,
  },
  // -----------------------------------------------------------------
  mutations: {
    setPaidPlans: (state, plans,) => (state.paidPlans = plans),
    setFreePlans: (state, plans,) => (state.freePlans = plans),
    setDetails: (state, plan,) => (state.details = plan),
    setHistory: (state, history,) => (state.history = history),
    setLoading: (state, isLoading,) => (state.isLoading = isLoading),
    setLoadingDetails: (state, isLoading,) => (state.isLoadingDetails = isLoading),
  },
  // -----------------------------------------------------------------
  actions: {
    /*
     * `has_trial` is a server-side filter, so each table is its own request.
     * They always load together, hence the single loading flag.
     */
    fetch: ({ commit, },) => {
      commit('setLoading', true,);

      return Promise.all([fetchGroup(false,), fetchGroup(true,),],).
        then(([paidPlans, freePlans,],) => {
          commit('setPaidPlans', paidPlans,);
          commit('setFreePlans', freePlans,);

          return { paidPlans, freePlans, };
        },).
        finally(() => commit('setLoading', false,),);
    },

    /*
     * Loads the full plan for the side modal. `hasTrial` comes from the row
     * that was clicked — the detail payload has no flag of its own, and the
     * model needs it to know which benefits list to read.
     *
     * The timeline is derived here rather than in the drawer, so the drawer
     * keeps consuming plain `{ created_at, periods }`.
     */
    fetchDetails: ({ commit, }, { id, hasTrial, },) => {
      commit('setLoadingDetails', true,);
      commit('setDetails', null,);
      commit('setHistory', null,);

      return _api.getById(id,).
        then((response,) => {
          const data = unwrapItem(response,);
          const plan = new SubscriptionPlan(data, { hasTrial, },);

          commit('setDetails', plan,);
          commit('setHistory', {
            created_at: plan.created_at,
            periods: buildActivationPeriods(data.audit_logs,),
          },);

          return plan;
        },).
        finally(() => commit('setLoadingDetails', false,),);
    },

    clearDetails: ({ commit, },) => {
      commit('setDetails', null,);
      commit('setHistory', null,);
    },

    /*
     * Only paid plans are exclusive: there is exactly 1 active, and the
     * backend does not swap on its own — it rejects enabling a second one
     * while another is enabled. So the swap is two sequential calls and the
     * disable has to come first.
     *
     * Free (trial) plans have no such limit: any number can be active at
     * once, so activating one is a single enable and must never disable
     * another plan.
     *
     * If the enable fails after a disable, the section would be left with no
     * active paid plan, so the previous one is restored and the original
     * error is rethrown, keeping the reason the operator needs to see.
     */
    activate: async ({ dispatch, getters, }, plan,) => {
      const active = plan.isPaid ? getters.activePaidPlan : null;
      const previous = active && active.id !== plan.id ? active : null;

      if (previous) {
        await _api.disable(previous.id,);
      }

      try {
        await _api.enable(plan.id,);
      } catch (error) {
        if (!previous) {
          throw error;
        }

        try {
          await _api.enable(previous.id,);
        } catch (rollbackError) {
          throw new Error(ROLLBACK_FAILED_MESSAGE,);
        }

        throw error;
      }

      return dispatch('fetch',);
    },

    deactivate: ({ dispatch, }, id,) => {
      return _api.disable(id,).then(() => dispatch('fetch',),);
    },
  },
};
